import React, { useEffect } from "react";
import { ArrowUpRight, Repeat, Anchor, Mountain } from "lucide-react";
import { SITE } from "@/lib/site";

const CIRCLES = [
  {
    icon: Repeat,
    name: "Operational Capital",
    pos: "Circle One",
    body:
      "Funds the daily work — canine care, handler training, deployments, and the working partnerships that produce presence in the field.",
    role: "Sustains today.",
  },
  {
    icon: Anchor,
    name: "Stabilization Capital",
    pos: "Circle Two",
    body:
      "Reserves that act as a shield against disruption. A six-month operating buffer that lets us continue the mission through any storm.",
    role: "Protects tomorrow.",
  },
  {
    icon: Mountain,
    name: "Legacy Capital",
    pos: "Circle Three",
    body:
      "Endowment for generational permanence. Planned gifts and major contributions secure the Coalition for the next 50 years and beyond.",
    role: "Secures the future.",
  },
];

export default function DonatePage() {
  useEffect(() => {
    document.title = "Support MSWDC · Donate";
  }, []);

  return (
    <main data-testid="donate-page" className="bg-white">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-amber-50/40">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 md:col-span-7">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Support the work</span>
              </div>
              <h1 className="mt-6 font-display font-black text-4xl sm:text-5xl md:text-7xl tracking-tighter leading-[1.02] text-slate-900">
                Fund the conditions
                <br />
                that prevent crisis.
              </h1>
              <p className="mt-8 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
                Your contribution sustains canine welfare, handler development, and the
                doctrine that makes every deployment reliable. Donations are processed
                securely through Givebutter — one-time or recurring.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <a
                  href={SITE.givebutterUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="donate-primary-cta"
                  className="group inline-flex items-center justify-center gap-3 bg-amber-600 hover:bg-amber-700 text-white px-8 py-5 font-semibold text-base transition-colors"
                >
                  Donate via Givebutter
                  <ArrowUpRight size={18} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
                <a
                  href={SITE.givebutterUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="donate-monthly-cta"
                  className="inline-flex items-center justify-center gap-3 border border-slate-300 hover:border-slate-900 text-slate-900 px-8 py-5 font-semibold text-base transition-colors"
                >
                  Give monthly
                </a>
              </div>

              <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Secure processing · 100% to the Coalition's mission
              </p>
            </div>

            <div className="col-span-12 md:col-span-5 md:pl-8">
              <div className="border border-slate-200 bg-white p-8 md:p-10">
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                  Why your gift matters
                </div>
                <blockquote className="mt-6 border-l-2 border-amber-600 pl-5">
                  <p className="font-display text-lg leading-snug text-slate-900">
                    "Sovereignty is measured not by revenue size but by freedom of
                    refusal."
                  </p>
                  <footer className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    Institutional Bible · Vol. III
                  </footer>
                </blockquote>
                <p className="mt-6 text-slate-600 text-[15px] leading-relaxed">
                  We diversify our funding deliberately. No single donor, grant, or
                  contract should ever have the leverage to alter our mission. Your
                  gift strengthens that independence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three-Circle Capital Model */}
      <section className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">The Three-Circle Capital Model</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Choose what your gift protects.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-10">
              <p className="text-slate-600 leading-relaxed">
                MSWDC is built for permanence. Every dollar lands in one of three
                circles — designed to sustain today, protect tomorrow, and secure the
                future.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {CIRCLES.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.name}
                  data-testid={`capital-circle-${i}`}
                  className="group relative border border-slate-200 bg-white p-8 md:p-10 hover:border-amber-600 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                      {c.pos}
                    </span>
                    <Icon size={22} strokeWidth={1.5} className="text-slate-400 group-hover:text-amber-600 transition-colors" />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl text-slate-900">{c.name}</h3>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                    {c.role}
                  </p>
                  <p className="mt-5 text-slate-600 leading-relaxed text-[15px]">{c.body}</p>
                  <a
                    href={SITE.givebutterUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid={`capital-circle-cta-${i}`}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-slate-900 link-underline group-hover:text-amber-700"
                  >
                    Give to {c.pos.toLowerCase()} <ArrowUpRight size={14} />
                  </a>
                </div>
              );
            })}
          </div>

          <div className="mt-12 border-t border-slate-200 pt-8 max-w-3xl">
            <p className="text-sm text-slate-500 leading-relaxed">
              For major gifts, planned giving, or to discuss directing your support to a
              specific circle, please{" "}
              <a href="/contact" className="link-underline text-amber-700">contact the Coalition</a>{" "}
              — we are honored to walk through the options with you.
            </p>
          </div>
        </div>
      </section>

      {/* Where your gift goes — operational specifics */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 md:col-span-6">
              <div className="flex items-center gap-3">
                <span className="brand-rule" />
                <span className="overline">Operationally, your gift funds</span>
              </div>
              <h2 className="mt-6 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05] text-slate-900">
                Doctrine, dogs, and the people who hold the leash.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {[
              { t: "Canine selection & training", b: "Sourcing, evaluation, and the disciplined months of preparation a working team requires." },
              { t: "Welfare & veterinary care", b: "Ongoing health, recovery, and ethical retirement. Welfare supremacy is non-negotiable." },
              { t: "Handler development", b: "Certification, continuing education, and the retention of qualified handlers." },
              { t: "Deployment & scale", b: "Equipment, transport, and the infrastructure that lets us serve more environments." },
            ].map((a, i) => (
              <div
                key={a.t}
                data-testid={`allocation-${i}`}
                className="border-l-2 border-slate-200 hover:border-amber-600 pl-6 transition-colors"
              >
                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-600">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-5 font-display font-bold text-xl text-slate-900">{a.t}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed text-[15px]">{a.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing band */}
      <section className="bg-[#0A1128] text-white grain">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-24 md:py-28">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-500">
                The Coalition stands with the people who fund it
              </div>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
                Stand watch with us.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <a
                href={SITE.givebutterUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="donate-footer-cta"
                className="inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-700 px-7 py-4 font-semibold text-sm"
              >
                Donate via Givebutter
                <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
